import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";

interface Props {
  onCreated?: () => void;
}

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function CreateTenant({ onCreated: _onCreated }: Props) {
  const authApi = useAuthApi();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleNameChange = (v: string) => {
    setName(v);
    if (!slugTouched) setSlug(toSlug(v));
  };

  const handleSlugChange = (v: string) => {
    setSlugTouched(true);
    setSlug(v.toLowerCase().replace(/[^a-z0-9-]/g, ""));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !slug.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await authApi<{ checkout_url: string }>(`${MGMT}/billing/checkout`, {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          slug: slug.trim(),
        }),
      });
      if (result?.checkout_url) {
        window.location.href = result.checkout_url;
      }
    } catch (err) {
      let msg = (err as Error).message;
      try { msg = JSON.parse(msg).detail ?? msg; } catch { /* keep raw */ }
      setError(msg);
      setLoading(false);
    }
    // Don't setLoading(false) on success — we're navigating away
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 3,
      }}
    >
      <Paper elevation={3} sx={{ maxWidth: 480, width: "100%", p: 5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
            }}
          >
            <ElectricBoltIcon />
          </Box>
          <Typography variant="h5" fontWeight={700}>
            Zennet
          </Typography>
        </Box>

        <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
          Create your organization
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          You&apos;ll be redirected to a secure checkout to start your subscription. Your
          organization will be provisioned automatically after payment.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack component="form" spacing={2.5} onSubmit={handleSubmit}>
          <TextField
            label="Organization name"
            placeholder="Acme Charging"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            required
            fullWidth
            autoFocus
            disabled={loading}
          />
          <TextField
            label="Slug"
            placeholder="acme-charging"
            value={slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            required
            fullWidth
            disabled={loading}
            helperText="Lowercase letters, numbers, and hyphens only. Used in API identifiers."
            slotProps={{ input: { sx: { fontFamily: "monospace" } } }}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={loading || !name.trim() || !slug.trim()}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : "Continue to payment →"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
